import { defaultTheme } from '../images/index';
import { lang } from '@umu/ukit-lang';
import {
    ICertificateThemeInfo,
    CERTIFICATE_IMG_TYPE,
    CERTIFICATE_TYPE,
    CERTIFICATE_SOURCE,
    CERTIFICATE_SOURCE_TYPE,
    CERTIFICATE_FORMAT,
} from '../interface/index';
import { $window } from '@umu/umu-dom';
import { customFormatDateTime } from '@umu/umu-util';
import { systemThemeTypeMap } from '../systemStudy/index';
import { IStudyThemeInfo } from '../interface/studyCertificate';

const COMMENT_MAX_LEN = 120;
const SYSTEM_TEMPLATE_QR_WIDTH = 66;
const DEFAULT_SCALE_RATIO = 1;
const defaultThemeInfo: ICertificateThemeInfo = {
    certificateImg: defaultTheme.redThemeImg,
    certificateType: CERTIFICATE_IMG_TYPE.IMG_TYPE,
    certificateName: '',
    traineesName: '',
    certificateText: '',
    enterpriseInfo: {
        logo: '',
        logoType: CERTIFICATE_IMG_TYPE.IMG_TYPE,
        issuingUnitName: '',
    },
    courseInfo: {
        courseName: '',
    },
    certificateDisplayConfig: {
        isShowValidityStartTime: false,
        isShowCertificateIssueTime: false,
        isShowCertificateQRCode: false,
        isShowCourseName: false,
        isShowSourceName: false,
        setUpInfo: {
            certificateIssueTime: '',
            certificateEffective: '',
            certificateEndTime: '',
            validPeriodTime: '',
            QRCodeUrl: 'https://umu.cn',
            certificateIsPermanent: true,
        },
    },
    certificateContentStyle: {
        width: '3.36rem',
        height: '4.69rem',
    },
};
const defaultSetUp = {
    effective_ts: '',
    grant_ts: '',
    expire_ts: '',
};
const studentInfoDefault = {
    umu_id: '',
    user_name: '',
};
const defaultTrophyInfoConfig = {
    isShow: true,
    viewType: 0,
    isEnterprise: false,
    cls: '',
    trophyElement: '',
};
/**
 * @desc 企业证书转换函数
 */
const getTranslateThemeInfo = (certificateInfo: any, expendData?: any): any => {
    const {
        certificate_base_info: baseInfo,
        relate_course_info,
        current_course_info,
        student_certificate_info: setUpInfo = defaultSetUp,
        student_info: studentInfo = studentInfoDefault,
    } = certificateInfo;
    const courseInfo = current_course_info || relate_course_info;

    const grantTs = setUpInfo.grant_ts;
    // 证书已颁发
    const isGranted = grantTs !== '' && !!Number(grantTs);
    // 证书颁发时间
    const certificateIssueTime = isGranted ? customFormatDateTime(grantTs * 1000) : lang('暂未获得');

    const effectiveTs = setUpInfo.effective_ts;
    // 证书已生效
    const isEffective = effectiveTs !== '' && !!Number(effectiveTs);
    // 证书生效时间
    const certificateEffective = isEffective ? customFormatDateTime(effectiveTs * 1000) : '';

    // 结束时间
    const expireTs = setUpInfo.expire_ts;
    const isPermanent = !(expireTs !== '' && Number(expireTs)); // 是否永久有效
    // 证书失效时间
    const certificateEndTime = isPermanent ? lang('永久有效') : customFormatDateTime(expireTs * 1000);

    // 学员企业证书暂未获取，并且学员证书到期时间expire_ts为0 (说明学员从未获取过该企业证书)，
    // 证书有效期限应该获取certificate_base_info里的valid_period进行展示
    const validPeriod = baseInfo.valid_period;
    let validPeriodTime = '';
    if (!expireTs && !isGranted && validPeriod) {
        validPeriodTime = lang('${0}个月').replace('${0}', validPeriod);
    }

    const themeInfo = {
        certificateImg: baseInfo.theme_background || defaultTheme.redThemeImg,
        certificateType: CERTIFICATE_IMG_TYPE.IMG_TYPE,
        certificateName: baseInfo.title,
        traineesName: studentInfo.user_name,
        traineesSubName: !!baseInfo.setup.student_number_switch ? studentInfo.number || '' : '',
        certificateText: baseInfo.text,
        enterpriseInfo: {
            logo: baseInfo.logo,
            logoType: CERTIFICATE_IMG_TYPE.IMG_TYPE,
            issuingUnitName: baseInfo.issue_enterprise,
        },
        courseInfo: {
            courseName: courseInfo.title,
        },
        certificateDisplayConfig: {
            isShowValidityStartTime: !!baseInfo.setup.period_switch,
            isShowValidityEndTime: !!baseInfo.setup.period_end_time_switch,
            isShowCertificateIssueTime: !!baseInfo.setup.issue_date_switch,
            isShowCertificateQRCode: !!baseInfo.setup.certificate_qr_code_switch,
            isShowCourseName: !!baseInfo.setup.course_name_switch,
            isShowSourceName: !!baseInfo?.setup?.source_name_switch,
            isShowLecturer: !!baseInfo.setup.show_lecturer_switch,
            setUpInfo: {
                QRCodeUrl: courseInfo.share_url,
                certificateIssueTime,
                certificateEffective,
                certificateEndTime,
                certificateIsPermanent: isPermanent,
                validPeriodTime,
            },
        },
        ...expendData,
    };
    return themeInfo;
};
/**
 *
 * @param themeType
 * @param themeStyleType // 组成形式 证书类型-样式下标
 * @returns 切割后的证书类型和样式下标
 */
const getThemeIndex = (themeType: CERTIFICATE_TYPE, themeStyleType: string) => {
    const result = {
        themeType,
        themeIndex: 1,
    };
    const arr = themeStyleType.split('-');
    if (arr && arr.length === 2) {
        if (Number(themeType) !== Number(arr[0])) {
            result.themeType = arr[0] as CERTIFICATE_TYPE;
        }
        const themeIndex = Number(arr[1] || '1') || 1;
        result.themeIndex = themeIndex - 1;
    }
    return result;
};
/**
 * @desc 获取证书类型 为了兼容不同证书类型和样式类型将证书类型与样式类型进行关联用中划线分割
 */
const getThemeStyleType = (themeType: CERTIFICATE_TYPE, themeId: string) => {
    return `${themeType}-${Number(themeType) === 2 ? '1' : systemThemeTypeMap[Number(themeId)] || 1}`;
};
/**
 * @desc 证书来源与模版同时模版类型为系统的时候为系统证书，否则为自定义学习证书
 */
const getStudyThemeType = (themeType: CERTIFICATE_SOURCE_TYPE, templateType: CERTIFICATE_SOURCE): CERTIFICATE_TYPE => {
    const isSystem =
        themeType === CERTIFICATE_SOURCE_TYPE.TEMPLATE && templateType === CERTIFICATE_SOURCE.SYSTEM_TEMPLATE;
    return isSystem ? CERTIFICATE_TYPE.SYSTEM_STUDY : CERTIFICATE_TYPE.CUSTOM_STUDY;
};
/**
 * @desc 根据课程id随机生成证书courseId
 */
const getDefaultThemeId = (courseId: string) => {
    const randIndex = !!courseId ? Number(courseId) : Math.round(Math.random() * 1000) + 1;
    const list: string[] = [];
    for (const keys in systemThemeTypeMap) {
        if (keys && systemThemeTypeMap[keys]) {
            list.push(keys);
        }
    }
    return list.length ? list[randIndex % list.length] : '42';
};
/**
 * @desc 学习证书转换函数，方便统一使用
 */
const getTranslateStudyThemeInfo = (themeInfo: IStudyThemeInfo, extendData?: any): ICertificateThemeInfo => {
    const result = {
        certificateImg: themeInfo.themeBackground,
        certificateType: 0,
        certificateId: themeInfo.themeId,
        themeType: themeInfo.themeType,
        displayStyle: themeInfo.displayStyle || CERTIFICATE_FORMAT.VERTICAL,
        themeStyleType: getThemeStyleType(themeInfo.themeType, themeInfo.themeId),
        certificateName: themeInfo.certificateName,
        traineesName: themeInfo.traineesName,
        traineesSubName: themeInfo.traineesSubName,
        certificateText: themeInfo.certificateText,
        enterpriseInfo: {
            logo: themeInfo.logo,
            logoType: 0,
            issuingUnitName: themeInfo.issuingUnitName,
        },
        courseInfo: {
            courseName: themeInfo.groupTitle,
        },
        certificateDisplayConfig: {
            isShowCertificateIssueTime: themeInfo.configInfo.isShowCertificateIssueTime,
            isShowCertificateQRCode: themeInfo.configInfo.isShowCertificateQRCode,
            isShowCourseName: themeInfo.configInfo.isShowCourseName,
            isShowLecturer: true,
            isShowTraineesSubName: themeInfo.configInfo.isShowTraineesSubName,
            isAllowEditor: themeInfo.configInfo.isAllowEditor,
            isShowSourceName: (themeInfo as any).configInfo.isShowSourceName,
            setUpInfo: {
                certificateIssueTime: themeInfo.certificateIssueTime,
                QRCodeUrl: themeInfo.qRCodeUrl,
                lecturerName: themeInfo.lecturerName,
                lecturerSubName: extendData.lecturerSubName || '',
            },
        },
        isCustomWidth: themeInfo.isCustomWidth,
        certificateWidth: themeInfo.isCustomWidth ? themeInfo.certificateWidth : 229,
        certificateHeight: themeInfo.isCustomWidth ? themeInfo.certificateHeight : 305,
        sourceData: themeInfo.sourceData,
        ...extendData,
    };
    return result as ICertificateThemeInfo;
};
function getPlatform(): 'mobile' | 'twww' {
    // M端只有m的域名上有
    const href = $window.location.href;
    if (href.indexOf('//test.m.') > -1) {
        return 'mobile';
    }
    if (href.indexOf('//m.') > -1 || href.indexOf('//pin.') > -1) {
        return 'mobile';
    }
    return 'twww';
}
const getStudyTemplateQrCodeParams = (scaleRatio: number, width?: number) => {
    const index = width || SYSTEM_TEMPLATE_QR_WIDTH;
    return {
        width: index * scaleRatio,
        height: index * scaleRatio,
    }
}
const getCertificateIssueTime = (time: number, text?: string) => {
    return time > 0 ? customFormatDateTime(time * 1000, lang('yyyy年MM月dd日')) : (lang('暂未获得') || text);
}

const onTransformProgramTheme = (data: any, expendData?: any) => {
    const { certificateInfo, isTeacher, getCertificateZoneArr, certificateWidth, certificateHeight, isSmall } = data;
    const { theme_info, finish_time = '', enterprise_user_number = '', user_name = '', finish_rate = 0 } = certificateInfo;
    const { theme_info: subThemeInfo, theme_type, theme_id = '', program_title = '', program_share_url = '' } = theme_info;
    const themeType = getStudyThemeType(
        Number(theme_type) || 1,
        Number(subThemeInfo.template_type),
    );
    const isFinish = Number(finish_rate) === 1;
    const isSystem = themeType === CERTIFICATE_TYPE.SYSTEM_STUDY;
    const displayStyle = Number(subThemeInfo.display_style || CERTIFICATE_FORMAT.VERTICAL);
    const isVertical = displayStyle === CERTIFICATE_FORMAT.VERTICAL;
    const [certificateWidthArr, certificateHeightArr] = isVertical ? [[188, 355], [251, 474]] : [[251, 474], [188, 355]];
    const certificateZoneIndex = isSmall ? 0 : 1;
    const certificateText =
        (isSystem && certificateInfo.tips) || subThemeInfo.text || (isSystem && lang('成功学完学习项目')) || '';
    const learningProgress = (isFinish || isTeacher) ? '' : (lang('必修完成度 ') + (finish_rate * 100).toFixed(0) + '%')
    const traineesSubName = isSystem ? learningProgress : enterprise_user_number || (isTeacher && !isFinish ? lang('学员工号') : '');
    const isShowTraineesSubName = isSystem
        ? !!learningProgress
        : !!subThemeInfo.setup.student_number_switch;
    const defaultLecturerName = isSystem ? certificateInfo.teacherName : '';
    const lecturerName = subThemeInfo.teacher_name || defaultLecturerName;
    const [customWidth, customHeight] = getCertificateZoneArr && getCertificateZoneArr(isVertical) || [certificateWidth, certificateHeight];
    const params = {
        themeBackground: subThemeInfo.theme_background || '',
        themeType: themeType,
        certificateName: subThemeInfo.title || '',
        themeId: theme_id,
        displayStyle,
        logo: subThemeInfo.logo,
        groupTitle: program_title,
        traineesName: isTeacher && !isFinish ? lang('学员姓名') : user_name,
        traineesSubName,
        certificateIssueTime: finish_time,
        certificateText,
        lecturerName,
        qRCodeUrl: program_share_url || '',
        issuingUnitName: subThemeInfo.issue_enterprise || '',
        isCustomWidth: true,
        certificateWidth: customWidth || certificateWidthArr[certificateZoneIndex],
        certificateHeight: customHeight || certificateHeightArr[certificateZoneIndex],
        configInfo: {
            isAllowEditor: false,
            isShowTraineesSubName,
            isShowCertificateQRCode: !!subThemeInfo.setup.certificate_qr_code_switch,
            isShowCertificateIssueTime: !!subThemeInfo.setup.certificate_date_switch,
            isShowCourseName: !!subThemeInfo.setup.program_name_switch,
        },
        sourceData: certificateInfo,
        ...expendData
    };
    return getTranslateStudyThemeInfo(params as any, {
        lecturerSubName: lang('学习项目讲师')
    });
};
const defaultCertificateWidth = 169; // 小默认证书宽度
const HorizontalWidth = 533; // 横版证书宽度
const VerticalWidth = 400; // 竖版证书宽度
const getCertificateShowWidth = (disPlayStyle = CERTIFICATE_FORMAT.VERTICAL, defaultWidth = defaultCertificateWidth, isMax = false) => {
    const docEle = document.querySelector('.doc');
    let docWidth = defaultWidth;
    const isHorizontal = disPlayStyle === CERTIFICATE_FORMAT.HORIZONTAL;
    if (docEle) {
        docWidth = docEle.clientWidth * 0.916 || docWidth;
        isMax && (docWidth = docWidth > defaultWidth ? docWidth : defaultWidth);
    }
    return isHorizontal ? [docWidth, docWidth * 0.75] : [docWidth, docWidth * (4 / 3)];
};
export {
    getThemeIndex,
    getPlatform,
    COMMENT_MAX_LEN,
    DEFAULT_SCALE_RATIO,
    defaultThemeInfo,
    defaultCertificateWidth,
    getDefaultThemeId,
    getThemeStyleType,
    getStudyThemeType,
    getTranslateThemeInfo,
    getCertificateShowWidth,
    onTransformProgramTheme,
    HorizontalWidth,
    VerticalWidth,
    defaultTrophyInfoConfig,
    getCertificateIssueTime,
    getStudyTemplateQrCodeParams,
    getTranslateStudyThemeInfo,
};
